/* eslint-disable no-console */
import React, { useState, useRef, useEffect, memo } from 'react';
import { Grid, Box, IconButton } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import { catchErr } from '../../../../../libs/HelperContent';
import { getConnectionsByFeeds } from '../../../../actions/feeds_actions_new';
import Loading from '../../../../../components/Common/Loaders/FullWidthLoader/Loading';
import ConnectionAvatar from './ConnectionAvatars';
interface ArrowButtonProps {
	position: 'left' | 'right';
}

const Connections = ({ setConnection, connectionData, setSelectedFilter,
	feedConnectionData, setFeedConnectionData }) =>
{
	const [ canScroll, setCanScroll ] = useState({ left: false, right: false });
	const [ isHovering, setIsHovering ] = useState(false);
	const [ loading, setLoading ] = useState(false);
	const [ visibleCount, setVisibleCount ] = useState(0);
	// const [ feedConnectionData, setFeedConnectionData ] = useState<any>([]);
	const navigate = useNavigate();
	const scrollContainerRef = useRef<any>(null);
	const connectionLimit = 20;
	const { type } = useParams();

	const fetchConnectionData = async () =>
	{
		if (!loading)
		{
			try
			{
				const queryParams = {
					skip  : 0,
					limit : connectionLimit
				};

				const res = await getConnectionsByFeeds(queryParams);

				if (res?.status)
				{
					setFeedConnectionData(res?.data);
					setLoading(false);
				}
				else
				{
					console.log('response failure', res);
					setLoading(false);
				}
				setLoading(false);
			}
			catch (error)
			{
				catchErr(error);
				setLoading(false);
			}
			finally
			{
				setLoading(false);
			}
		}
	};

	useEffect(() =>
	{
		fetchConnectionData();
	}, []);

	const checkScrollability = () =>
	{
		if (scrollContainerRef.current)
		{
			const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

			setCanScroll({
				left  : scrollLeft > 0,
				right : scrollLeft + clientWidth < scrollWidth
			});
		}
	};

	const calculateVisibleCount = () =>
	{
		if (scrollContainerRef.current)
		{
			const { clientWidth } = scrollContainerRef.current;
			const cardWidth = 100;
			const margin = 20;

			if (cardWidth > 0 && margin >= 0)
			{
				const count = Math.floor(clientWidth / (cardWidth + margin));

				setVisibleCount(count);
			}
			else
			{
				console.error('Invalid cardWidth or margin values');
			}
		}
	};

	useEffect(() =>
	{
		calculateVisibleCount();
		checkScrollability();

		const handleResize = () =>
		{
			calculateVisibleCount();
			checkScrollability();
		};

		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, [ feedConnectionData ]);

	const handleScroll = (direction: 'left' | 'right') =>
	{
		const container = scrollContainerRef.current;

		if (container)
		{
			const scrollAmount = direction === 'left' ? -200 : 200;

			container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
			setTimeout(checkScrollability, 100);
		}
	};

	// ArrowButton styled component
	const ArrowButton = styled(IconButton)<ArrowButtonProps>(({ position }) => ({
		position   : 'absolute',
		top        : 'var(--percent50)',
		transform  : 'translateY(-50%)',
		zIndex     : 3,
		background : 'rgba(116, 116, 116, 0.7)',
		transition : 'opacity 0.3s',
		opacity    : isHovering ? 1 : 0,
		...(position === 'left' ? { left: 0 } : { right: 0 }),
		'&:hover'  : {
			background : 'rgba(116, 116, 116, 0.7)'
		}
	}));

	const handleSelect = (id:any) =>
	{
		if (connectionData?.userId === id)
		{
			setConnection([]);
			navigate('/news');
		}
		else
		{
			setConnection({ userId: id, type: 'connection' });
			navigate('/news/connections');
		}
		setSelectedFilter({});
	};

	useEffect(() =>
	{
		return () =>
		{
			if (type === 'connections')
			{
				setConnection([]);
			}
		};
	}, [ type ]);

	return (
		<>
			{loading && <Loading />}
			<Grid item xs={12}>
				<Box
					sx={{ position: 'relative', width: 'var(--percent100)', overflow: 'hidden' }}
					onMouseEnter={() => setIsHovering(true)}
					onMouseLeave={() => setIsHovering(false)}
				>
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Box
							ref={scrollContainerRef}
							sx={{
								display                : 'flex',
								overflowX              : 'auto',
								scrollbarWidth         : 'none',
								'&::-webkit-scrollbar' : { display: 'none' },
								transition             : 'all 0.3s ease',
								flexGrow               : 1
							}}
							onScroll={checkScrollability}
						>
							<Grid container wrap='nowrap' pt={1}>
								{feedConnectionData?.map((item: any, index: any) => (
									<ConnectionAvatar
										key={item?.companyId}
										index={index}
										item={item}
										connectionData={connectionData}
										handleSelect={handleSelect}
									/>
								))}
							</Grid>

						</Box>
					</Box>
					{((canScroll.left || canScroll.right) ||
					(feedConnectionData.length > visibleCount)) &&
					(
						<Box className='arrows-container'>
							<ArrowButton
								position='left'
								onClick={() => handleScroll('left')}
								disabled={!canScroll.left}
							>
								<KeyboardArrowLeftIcon sx={{ color: 'var(--whiteColor)' }} />
							</ArrowButton>
							<ArrowButton
								position='right'
								onClick={() => handleScroll('right')}
								disabled={!canScroll.right}
							>
								<KeyboardArrowRightIcon sx={{ color: 'var(--whiteColor)' }} />
							</ArrowButton>
						</Box>
					)}
				</Box>
			</Grid>
		</>
	);
};

export default memo(Connections);
